import React, { useState, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShieldAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import RolePermissionModal from './RolePermissionModal';
import { createRole } from '../services/authorizationService';
import { Modal, ModalHeader } from '../../../globalComponents/ui/Modal';
import styled from 'styled-components';

const PageWrap = styled.div`
  padding: 12px 0;
  background: transparent;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
  h4 {
    font-size: 1.3rem;
    font-weight: 700;
    color: #1a2e38;
    margin-bottom: 2px;
  }
  span {
    font-size: 0.875rem;
    color: #6b8794;
  }
`;

const SearchWrap = styled.div`
  position: relative;
  width: 300px;
  .icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b8794;
    font-size: 0.8rem;
  }
  input {
    width: 100%;
    padding: 8px 12px 8px 34px;
    border: 1.5px solid #e2eaf0;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #1a2e38;
    background: #fff;
    outline: none;
    transition: border-color 0.2s;
    &:focus { border-color: #3f88a5; }
  }
`;

const TableCard = styled.div`
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e2eaf0;
  box-shadow: 0 2px 8px rgba(63,136,165,0.06);
  overflow: hidden;
`;

const TableToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f4f7;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;

  thead tr {
    background: #f8fafc;
    border-bottom: 2px solid #e2eaf0;
  }

  th {
    padding: 12px 20px;
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b8794;
    text-align: left;
    white-space: nowrap;
    user-select: none;
    cursor: pointer;
    &:hover { color: #3f88a5; }
  }

  tbody tr {
    border-bottom: 1px solid #f0f4f7;
    transition: background 0.15s;
    &:hover { background: #f7fafc; }
    &:last-child { border-bottom: none; }
  }

  td {
    padding: 14px 20px;
    font-size: 0.9rem;
    color: #1a2e38;
    vertical-align: middle;
  }
`;

const SystemBadge = styled.span`
  display: inline-block;
  background: #e8f4f8;
  color: #3f88a5;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-left: 8px;
`;

const ViewBtn = styled.button`
  background: #f0f7fa;
  border: 1px solid #d0e6ef;
  color: #3f88a5;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.18s;
  &:hover {
    background: #3f88a5;
    color: #fff;
    border-color: #3f88a5;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #6b8794;
  svg { font-size: 2rem; margin-bottom: 12px; opacity: 0.4; display: block; margin-inline: auto; }
  p { font-size: 0.95rem; margin: 0; }
`;

const CreateBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #3f88a5;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 18px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s;
  &:hover { background: #2d6a84; }
`;

const ModalBody = styled.div`
  padding: 20px 24px 24px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e2eaf0;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #1a2e38;
  outline: none;
  transition: border-color 0.2s;
  margin-bottom: 16px;
  &:focus { border-color: #3f88a5; box-shadow: 0 0 0 3px rgba(63,136,165,0.12); }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e2eaf0;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #1a2e38;
  outline: none;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 80px;
  &:focus { border-color: #3f88a5; box-shadow: 0 0 0 3px rgba(63,136,165,0.12); }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid #edf2f7;
  margin-top: 8px;
`;

const CancelBtn = styled.button`
  padding: 8px 20px;
  border: 1.5px solid #e2eaf0;
  border-radius: 6px;
  background: #fff;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { background: #f7fafc; }
`;

const SaveBtn = styled.button`
  padding: 8px 24px;
  border: none;
  border-radius: 6px;
  background: #3f88a5;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: #2d6a84; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.div`
  background: #fff5f5;
  border: 1px solid #fed7d7;
  color: #c53030;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 0.85rem;
  margin-bottom: 14px;
`;

const SYSTEM_ROLES = ['Admin', 'Internal Auditor', 'External Auditor', 'Data Owner'];

const RolesList = ({ roles, isLoading, onRefetch }) => {
  const [viewRole, setViewRole] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);

  // Create role state
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [newRole, setNewRole] = useState({ name: '', description: '' });

  const handleOpenCreate = () => {
    setNewRole({ name: '', description: '' });
    setCreateError(null);
    setShowCreate(true);
  };

  const handleCloseCreate = () => {
    setShowCreate(false);
    setCreateError(null);
  };

  const handleCreateRole = async () => {
    if (!newRole.name.trim()) {
      setCreateError('Role name is required.');
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await createRole({ name: newRole.name.trim(), description: newRole.description.trim() });
      setShowCreate(false);
      onRefetch?.();
    } catch (e) {
      setCreateError(e?.message || 'Failed to create role. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const uniqueRoles = useMemo(() => {
    if (!roles) return [];
    const seen = new Set();
    return roles.filter(r => {
      const key = r.id || r.name;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [roles]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let list = q
      ? uniqueRoles.filter(r =>
          (r.displayName || r.name)?.toLowerCase().includes(q) ||
          r.description?.toLowerCase().includes(q)
        )
      : uniqueRoles;

    list = [...list].sort((a, b) => {
      const getVal = (x) => {
        if (sortKey === 'name') {
          return x.displayName || x.name || '';
        }
        return x[sortKey] || '';
      };
      const av = getVal(a).toLowerCase();
      const bv = getVal(b).toLowerCase();
      return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return list;
  }, [uniqueRoles, search, sortKey, sortAsc]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc(p => !p);
    else { setSortKey(key); setSortAsc(true); }
  };

  const sortIcon = (key) => sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '';

  if (isLoading) {
    return (
      <PageWrap>
        <div className="auth-loading">
          <Spinner animation="border" style={{ color: '#3f88a5' }} />
          <p>Loading roles...</p>
        </div>
      </PageWrap>
    );
  }

  return (
    <PageWrap>
      <PageHeader>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h4>Roles</h4>
            <span>{uniqueRoles.length} total roles</span>
          </div>
          <CreateBtn onClick={handleOpenCreate}>
            <FontAwesomeIcon icon={faPlus} />
            Create Role
          </CreateBtn>
        </div>
      </PageHeader>

      <TableCard>
        <TableToolbar>
          <span style={{ fontWeight: 600, color: '#1a2e38', fontSize: '0.95rem' }}>All Roles</span>
          <SearchWrap>
            <FontAwesomeIcon icon={faSearch} className="icon" />
            <input
              placeholder="Search roles…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </SearchWrap>
        </TableToolbar>

        <StyledTable>
          <thead>
            <tr>
              <th style={{ width: '30%' }} onClick={() => toggleSort('name')}>
                Role Name{sortIcon('name')}
              </th>
              <th onClick={() => toggleSort('description')}>
                Description{sortIcon('description')}
              </th>
              <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  <EmptyState>
                    <FontAwesomeIcon icon={faShieldAlt} />
                    <p>{uniqueRoles.length === 0 ? 'No roles found.' : 'No roles match your search.'}</p>
                  </EmptyState>
                </td>
              </tr>
            ) : (
              filtered.map(role => {
                const isSystem = role.isSystem || SYSTEM_ROLES.includes(role.name);
                return (
                  <tr key={role.id || role.name}>
                    <td>
                      <span style={{ fontWeight: 600 }}>{role.displayName || role.name}</span>
                      {isSystem && <SystemBadge>System</SystemBadge>}
                    </td>
                    <td style={{ color: '#475569' }}>{role.description || <span style={{ color: '#aab8c2' }}>—</span>}</td>
                    <td style={{ textAlign: 'center' }}>
                      <ViewBtn onClick={() => setViewRole(role)}>
                        <FontAwesomeIcon icon={faShieldAlt} />
                        Manage
                      </ViewBtn>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </StyledTable>
      </TableCard>

      <RolePermissionModal
        show={!!viewRole}
        onHide={() => setViewRole(null)}
        role={viewRole}
        onSaveSuccess={onRefetch}
      />

      {/* Create Role Modal */}
      <Modal show={showCreate} onClose={handleCloseCreate} maxWidth={480}>
        <ModalHeader
          title="Create New Role"
          subtitle="Define a new role. Capabilities can be assigned after creation."
          onClose={handleCloseCreate}
        />
        <ModalBody>
          {createError && <ErrorMsg>{createError}</ErrorMsg>}

          <div>
            <FormLabel>Role Name *</FormLabel>
            <FormInput
              placeholder="e.g. Data Reviewer"
              value={newRole.name}
              onChange={(e) => {
                const val = e.target.value;
                setNewRole(p => ({ ...p, name: val }));
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateRole()}
              autoFocus
            />
          </div>

          <div>
            <FormLabel>Description</FormLabel>
            <FormTextarea
              placeholder="Briefly describe what this role can do…"
              value={newRole.description}
              onChange={(e) => {
                const val = e.target.value;
                setNewRole(p => ({ ...p, description: val }));
              }}
              rows={3}
            />
          </div>

          <ModalFooter>
            <CancelBtn onClick={handleCloseCreate} disabled={creating}>Cancel</CancelBtn>
            <SaveBtn onClick={handleCreateRole} disabled={creating || !newRole.name.trim()}>
              {creating && <Spinner animation="border" size="sm" />}
              Create Role
            </SaveBtn>
          </ModalFooter>
        </ModalBody>
      </Modal>



    </PageWrap>
  );
};

export default RolesList;
